import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";

// Standard-Ansicht beim Öffnen einer bestehenden Aufgabe (Kanban-Karte-Klick).
// "compact"  = schlanker Body (Titel/Status/Fällig/Workstream/Beschreibung).
// "detailed" = voller Body (Ziel/Acceptance/Unteraufgaben/Zuweisung/Activity).
// Default "detailed" = aktuelles Verhalten, keine Verhaltensänderung für Bestand.
export type TaskViewMode = "compact" | "detailed";

// Nur die Werte. Beschriftung + Hinweis stehen im Katalog
// (`settings.aufgabenAnsicht[wert]`).
export const TASK_VIEW_OPTIONS: { value: TaskViewMode }[] = [
  { value: "compact" },
  { value: "detailed" },
];

const STORAGE_KEY = "sb_task_default_view";
const DEFAULT_VALUE: TaskViewMode = "detailed";

interface Ctx {
  view: TaskViewMode;
  setView: (v: TaskViewMode) => void;
}

const TaskViewPrefCtx = createContext<Ctx | null>(null);

function isValid(v: unknown): v is TaskViewMode {
  return v === "compact" || v === "detailed";
}

function getInitial(): TaskViewMode {
  const saved = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  return isValid(saved) ? saved : DEFAULT_VALUE;
}

export function TaskViewPrefProvider({ children }: { children: ReactNode }) {
  const [view, setViewRaw] = useState<TaskViewMode>(getInitial);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, view);
  }, [view]);

  const setView = useCallback((v: TaskViewMode) => setViewRaw(v), []);

  return <TaskViewPrefCtx.Provider value={{ view, setView }}>{children}</TaskViewPrefCtx.Provider>;
}

export function useTaskViewPref(): Ctx {
  const v = useContext(TaskViewPrefCtx);
  if (!v) throw new Error("useTaskViewPref outside TaskViewPrefProvider");
  return v;
}
